/**
 * @module app.cd
 */

import { autoinject, transient } from "aurelia-framework";
import { Api } from 'app/shared/lib/api/api';
import { IQueryLoadOptions, IApiQueryResults } from 'app/shared/lib/api/api-types';
import * as CD from 'app/cd/models/essette-api';

export interface IServiceSearchFilters {
    isDental?: boolean;
    codeOrDescription?: string | null;
}

/**
 * Provides access to general CD lookup API models
 */
@transient()
@autoinject
export class LookupService {

    ////****************** Fields and Properties ************************************////


    ////****************** Functions ************************************************////
    constructor(protected readonly api: Api) { }


    ////****************** Loads/Queries ********************************************////

    /**
     * Load all `CD.BodySite` models
     * @param options query load options
     * @param includes related models to include
     * @param bodySiteTypeCode body site type code to filter
     */
    public async loadBodySites(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = [], bodySiteTypeCode: string | null = null): Promise<IApiQueryResults<CD.BodySite>> {
        const query = this.api
            .query('cd/bodysites')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(!!bodySiteTypeCode) query.filterString('bodySiteType.code', bodySiteTypeCode, 'eq');

        return await this.api.get(query);
    }

    /**
     * Load all `CD.BodySubSite` models
     * @param options query load options
     * @param includes related models to include
     * @param bodySiteTypeCode body site type code to filter
     */
    public async loadBodySubSites(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = [], bodySiteTypeCode: string | null = null): Promise<IApiQueryResults<CD.BodySubSite>> {
        const query = this.api
            .query('cd/bodysubsites')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(!!bodySiteTypeCode) query.filterString('bodySiteType.code', bodySiteTypeCode, 'eq');

        return await this.api.get(query);
    }

    /**
     * Search `CD.ClaimModifier` models by code
     * @param code search query string
     * @param options query load options
     * @param includes related models to include
     */
    public async loadClaimModifiers(code?: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.ClaimModifier>> {
        const query = this.api
            .query('cd/claims/modifiers')
            .applyStandardLoadOptions(options)
            .include(...includes);

        if(code) {
            query.filterString('code', code, 'eq');
        }

        return await this.api.get(query);
    }

    /**
     * Search `CD.Diagnosis` models
     * @param searchValue search query string
     * @param searchDate search date string
     * @param options query load options
     * @param includes related models to include
     */
    public async searchDiagnoses(searchValue: string, searchDate?: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.Diagnosis>> {
        const query = this.api
            .query('cd/diagnoses')
            .strategy('quick')
            .search(searchValue)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(searchDate) {
            query.filterDirect(`(date'${searchDate.substring(0, 10)}' in dateRange) or (dateRange.end eq null and date'${searchDate.substring(0, 10)}' gte dateRange.start)`);
        }

        return await this.api.get(query);
    }

    /**
     * Load all `CD.DiagnosisType` models
     * @param options query load options
     */
    public async loadDiagnosisTypes(options?: Partial<IQueryLoadOptions>): Promise<IApiQueryResults<CD.DiagnosisType>> {
        const query = this.api
            .query('cd/diagnoses/types')
            .applyStandardLoadOptions(options)
            .include('mappings', 'mappings.externalsystem');
        return await this.api.get(query);
    }

    /**
     * Load all `CD.HealthPlan` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadHealthPlans(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.HealthPlan>> {
        const query = this.api
            .query('cd/healthplans')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async searchHealthPlans(searchValue: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.HealthPlan>> {
        const query = this.api
            .query('cd/healthplans')
            .strategy('search')
            .applyStandardLoadOptions(options)
            .search(searchValue)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Load all `CD.LineOfBusiness` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadLinesOfBusiness(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.LineOfBusiness>> {
        const query = this.api
            .query('cd/linesOfBusiness')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async searchLinesOfBusiness(searchValue: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.LineOfBusiness>> {
        const query = this.api
            .query('cd/linesOfBusiness')
            .strategy('search')
            .applyStandardLoadOptions(options)
            .search(searchValue)
            .include(...includes);
        return await this.api.get(query);
    }

    public async searchProducts(healthPlanID: string, lobID: string, searchValue: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.LineOfBusiness>> {
        const query = this.api
            .query('cd/products')
            .strategy('search')
            .applyStandardLoadOptions(options)
            .search(searchValue)
            .include(...includes);
        query.filterString('healthPlan.id', healthPlanID, 'eq');
        query.filterString('lineOfBusiness.id', lobID, 'eq');

        return await this.api.get(query);
    }

    /**
     * Search `CD.PlaceOfService` models by code
     * @param code search query string
     * @param options query load options
     * @param includes related models to include
     */
    public async loadPlacesOfService(code?: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.PlaceOfService>> {
        const query = this.api
            .query('cd/placesofservice')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(code) {
            query.filterString('code', code, 'eq');
        }

        return await this.api.get(query);
    }

    /**
     * Search `CD.Service` models
     * @param searchValue search query string
     * @param options query load options
     * @param includes related models to include
     * @param quick use quick strategy
     * @param serviceSearchFilters filters
     */
    public async searchServices(searchValue?: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], quick: boolean = false, serviceSearchFilters?: IServiceSearchFilters, fields: string[] = []): Promise<IApiQueryResults<CD.Service>> {
        const query = this.api
            .query('cd/services')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(searchValue) {
            query.search(searchValue);
        }
        if(quick) {
            query.strategy('quick');
        }

        if(serviceSearchFilters) {
            if(serviceSearchFilters.isDental) {
                query.filterString('type.bodySiteType.code', 'Dental', 'eq');
            } else {
                query.filterOr(query => {
                    query.filterString('type.bodySiteType.code', 'Dental', 'ne');
                    query.filterIsNull('type.bodySiteType.code');
                });
            }
            if(serviceSearchFilters.codeOrDescription) {
                query.filterOr(query => {
                    query.filterString('code', serviceSearchFilters.codeOrDescription!, 'startsWith');
                    query.filterString('description', serviceSearchFilters.codeOrDescription!, 'startsWith');
                });
            }
        }

        return await this.api.get(query);
    }

    /**
     * Load all `CD.ServiceType` models
     * @param options query load options
     */
    public async loadServiceTypes(options?: Partial<IQueryLoadOptions>): Promise<IApiQueryResults<CD.ServiceType>> {
        const query = this.api
            .query('cd/services/types')
            .applyStandardLoadOptions(options)
            .include('mappings', 'mappings.externalsystem');
        return await this.api.get(query);
    }

    /**
 * Load all `Core.maritalStatus` models
 * @param options query load options
 * @param includes related models to include
 */
    public async loadMaritalStatuses(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.MaritalStatus>> {
        const query = this.api
            .query('CD/MaritalStatuses')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    /**
     * Load all `Core.Race` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadRaces(options?: Partial<IQueryLoadOptions>, includes: string[] = [], availableAsIdentity: boolean = false, availableAsBirthRace: boolean = false, fields: string[] = []): Promise<IApiQueryResults<CD.Race>> {
        const query = this.api
            .query('cd/races')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        if(availableAsIdentity) query.filterBoolean('flag.availableAsIdentity', true);
        if(availableAsBirthRace) query.filterBoolean('flag.availableAsBirthRace', true);

        return await this.api.get(query);
    }

    /**
     * Load all `Core.Ethnicity` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadEthnicities(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.Ethnicity>> {
        const query = this.api
            .query('cd/ethnicities')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadAidCodes(includes: string[] = [], options?: Partial<IQueryLoadOptions>, fields: string[] = []): Promise<IApiQueryResults<CD.AidCode>> {
        const query = this.api
            .query(`cd/aidCodes/`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields)

        return await this.api.get(query);
    }

    public async loadConditions(includes: string[] = [], options?: Partial<IQueryLoadOptions>, fields: string[] = []): Promise<IApiQueryResults<CD.Condition>> {
        const query = this.api
            .query(`cd/conditions/`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields)

        return await this.api.get(query);
    }

    ////****************** Actions **************************************************////

}
